import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DoCheck,
  EventEmitter,
  inject,
  Inject,
  Output,
} from "@angular/core";
import { MAT_DIALOG_DATA, MatDialog } from "@angular/material/dialog";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { RoleAccessData } from "@bitwarden/web-vault/app/models/data/role-access.data";
import { RoleAccessService } from "@bitwarden/web-vault/app/services/permission/role-access.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { SignalGlowService } from "@bitwarden/web-vault/app/services/signal-glow/signal-glow.service";
import {
  AccountManualType,
  accountTypeTitleMap,
} from "@bitwarden/web-vault/app/models/types/account.types";
import { UpgradeNavigationService } from "@bitwarden/web-vault/app/services/subscription/upgrade-navigation.service";

@Component({
  selector: "app-create-account-sync",
  templateUrl: "./account-sync.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AccountSyncComponent implements DoCheck {
  @Output() backClicked = new EventEmitter<void>();
  private roleAccess: RoleAccessData;
  protected userStoreService = inject(UserStoreService);
  protected signalGlowService = inject(SignalGlowService);
  protected upgradeNavigation = inject(UpgradeNavigationService);
  private i18nService = inject(I18nService);
  // activeSubscription() is null before the subscription loads and after logout (the store's
  // clear()); null means not premium.
  protected hasPremiumSubscription = computed(() => {
    return this.userStoreService.subscription.activeSubscription()?.isPremium ?? false;
  });
  hasScope: boolean;

  // Translated up-front so the ng-template contexts bind plain strings (the i18n pipe
  // doesn't read naturally inside an ngTemplateOutletContext object literal).
  protected fetchAutomaticallyTitle = this.i18nService.t("fetchAutomatically");
  protected fetchAutomaticallyDescription = this.i18nService.t("fetchAutomaticallyDescription");
  protected addManuallyTitle = this.i18nService.t("addManually");
  protected addManuallyDescription = this.i18nService.t("addManuallyDescription");

  protected readonly pageTitle: string;

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public data: {
      accountType?: AccountManualType;
      createManually: CallableFunction;
      createByLinking: CallableFunction;
      closeDialog: CallableFunction;
      backClicked: CallableFunction;
      isInWizard: boolean;
    },
    private roleAccessService: RoleAccessService,
    public dialog: MatDialog,
  ) {
    this.pageTitle = this.i18nService.t(
      this.data.accountType ? accountTypeTitleMap[this.data.accountType] : "addNewAccount",
    );
  }

  ngDoCheck() {
    if (!this.roleAccess) {
      this.roleAccess = this.roleAccessService.getRoleAccess();
      this.hasScope = this.hasBasiqAccess();
    }
  }

  hasBasiqAccess() {
    if (this.roleAccess) {
      const scope = this.roleAccess.getScope();
      const claim = this.roleAccess.getClaim();
      const dataAggregator = claim.data_aggregator_connector_name;

      return (
        (scope.includes("BETA_ACCESS") || scope.includes("DATA_AGGREGATOR")) &&
        dataAggregator.includes("BASIQ")
      );
    }
  }

  createManually() {
    this.data.createManually();
  }

  backToAccountCreateMain() {
    this.data.backClicked();
  }

  async createByLinking() {
    if (this.hasPremiumSubscription()) {
      this.data.createByLinking();
    }
  }
}
